/**
 * PATH: apps/quote/src/components/builder/unified-bottom-bar/StatusWidgets.tsx
 * PURPOSE: SaveStatusIndicator, CompactTotals, KeyboardHintsRow widgets
 */

import { Check, Loader2, AlertCircle } from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import { useWorkspace } from '../BuilderWorkspace'

export function SaveStatusIndicator() {
  const { saveStatus, lastSaved } = useWorkspace()
  const formatTimeAgo = (date: Date): string => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000)
    if (seconds < 5) return 'just now'
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    return date.toLocaleTimeString()
  }
  const getStatusDisplay = () => {
    switch (saveStatus) {
      case 'saving': return { icon: Loader2, text: 'Saving...', className: 'text-blue-500', animate: true }
      case 'saved': return { icon: Check, text: lastSaved ? formatTimeAgo(lastSaved) : 'Saved', className: 'text-emerald-500', animate: false }
      case 'error': return { icon: AlertCircle, text: 'Error', className: 'text-red-500', animate: false }
      default: return { icon: Check, text: 'Ready', className: 'text-slate-400', animate: false }
    }
  }
  const status = getStatusDisplay()
  const Icon = status.icon
  return (<div className={cn('flex items-center gap-1 text-xs', status.className)}><Icon className={cn('w-3.5 h-3.5', status.animate && 'animate-spin')} /><span className="hidden md:inline">{status.text}</span></div>)
}

export function CompactTotals({ totals }: { totals?: { totalEx: number; marginPercent: number; manDays: number } }) {
  if (!totals) return null
  const getMarginColor = (margin: number) => margin >= 25 ? 'text-emerald-600 dark:text-emerald-400' : margin >= 15 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
  return (
    <div className="flex items-center gap-1.5 sm:gap-2 text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded flex-shrink-0">
      <span className="hidden sm:inline text-slate-500 dark:text-slate-400">Total:</span>
      <span className="font-semibold text-slate-900 dark:text-white">{formatCurrency(totals.totalEx)}</span>
      <span className="text-slate-300 dark:text-slate-600">|</span>
      <span className="hidden sm:inline text-slate-500 dark:text-slate-400">Margin:</span>
      <span className={cn('font-semibold', getMarginColor(totals.marginPercent))}>{totals.marginPercent ? `${totals.marginPercent.toFixed(0)}%` : '—'}</span>
      <span className="text-slate-300 dark:text-slate-600">|</span>
      <span className="hidden sm:inline text-slate-500 dark:text-slate-400">Days:</span>
      <span className="text-slate-700 dark:text-slate-300">{totals.manDays?.toFixed(1) || '—'}</span>
    </div>
  )
}

export function KeyboardHintsRow() {
  const { showKeyboardHints } = useWorkspace()
  if (!showKeyboardHints) return null
  return (
    <div className="px-3 py-1 bg-slate-50 dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
      <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-slate-400 overflow-x-auto">
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">↑↓←→</kbd> Navigate</span>
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Enter</kbd> Edit/Down</span>
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Tab</kbd> Next cell</span>
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">F2</kbd> Edit</span>
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Ctrl+V</kbd> Paste</span>
        <span><kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded">Ctrl+D</kbd> Fill</span>
      </div>
    </div>
  )
}
